import { Link } from "react-router-dom";
import { ArrowRight, Dna, Sigma, Microscope, AlertTriangle } from "lucide-react";

const SECTIONS = [
  {
    n: "01",
    title: "Genes, alleles & loci",
    icon: Dna,
    body: (
      <>
        <p>
          A <strong>gene</strong> is a stretch of DNA that codes for a trait.
          Each gene sits at a fixed location called a <strong>locus</strong>.
          The alternative versions of a gene are called{" "}
          <strong>alleles</strong> — for example, the budgie base-colour locus
          has two alleles: <span className="genotype-chip">B</span> (green,
          dominant) and <span className="genotype-chip">b</span> (blue,
          recessive).
        </p>
        <p>
          Budgies inherit one allele from each parent at every autosomal locus,
          giving them a <strong>genotype</strong> (the actual allele pair, e.g.{" "}
          <span className="genotype-chip">Bb</span>) and a{" "}
          <strong>phenotype</strong> (what we see — in this case, green,
          because B dominates b).
        </p>
      </>
    ),
  },
  {
    n: "02",
    title: "Dominant, recessive & incomplete dominance",
    icon: Sigma,
    body: (
      <>
        <p>
          A <strong>dominant</strong> allele expresses its trait with only one
          copy ( <span className="genotype-chip">Aa</span> looks the same as{" "}
          <span className="genotype-chip">AA</span>). A{" "}
          <strong>recessive</strong> allele needs two copies (
          <span className="genotype-chip">aa</span>) to be visible.
        </p>
        <p>
          <strong>Incomplete dominance</strong> is dose-dependent: heterozygous
          birds look <em>between</em> the two homozygous extremes. The
          budgerigar <strong>dark factor</strong> is the classic example:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
          <li>
            <span className="genotype-chip">dd</span> Light green (or sky blue)
          </li>
          <li>
            <span className="genotype-chip">Dd</span> Dark green (or cobalt)
          </li>
          <li>
            <span className="genotype-chip">DD</span> Olive (or mauve)
          </li>
        </ul>
        <p>
          Each dose deepens the body colour. Violet, spangle and crest behave
          similarly.
        </p>
      </>
    ),
  },
  {
    n: "03",
    title: "Sex-linkage: ZW chromosomes",
    icon: Microscope,
    body: (
      <>
        <p>
          Birds, unlike mammals, use the <strong>ZW system</strong>. Cocks are{" "}
          <strong>ZZ</strong>; hens are <strong>ZW</strong>. Recessive
          mutations on the Z chromosome — <strong>Ino</strong>,{" "}
          <strong>Cinnamon</strong> and <strong>Opaline</strong> — therefore
          behave differently between sexes.
        </p>
        <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
          <li>
            A cock needs <strong>two</strong> recessive Z alleles to express
            (e.g. <span className="genotype-chip">ii</span> = lutino).
          </li>
          <li>
            A hen needs <strong>only one</strong>, because her single Z has no
            partner ( <span className="genotype-chip">i/W</span> = lutino).
          </li>
          <li>
            A hen <em>cannot</em> be &ldquo;split&rdquo; for a sex-linked recessive — she
            either shows it or she doesn&rsquo;t.
          </li>
        </ul>
        <p>
          This is why pairing a normal cock split-for-ino to a lutino hen
          produces lutino daughters and split-ino sons.
        </p>
      </>
    ),
  },
  {
    n: "04",
    title: "Splits, carriers & how to spot them",
    icon: AlertTriangle,
    body: (
      <>
        <p>
          A <strong>split</strong> bird is heterozygous for a recessive
          mutation: it carries the allele but doesn&rsquo;t show it. Splits are
          written with a slash: <em>Light green / blue</em> means a green bird
          carrying one blue allele.
        </p>
        <p>
          You can only confirm a split by <strong>test breeding</strong>:
          pairing the suspected carrier with a visual bird of that mutation and
          looking for visual chicks.
        </p>
      </>
    ),
  },
  {
    n: "05",
    title: "Reading a Punnett square",
    icon: Sigma,
    body: (
      <>
        <p>
          A Punnett square is just a grid of <em>parent A&rsquo;s possible gametes</em>{" "}
          × <em>parent B&rsquo;s possible gametes</em>. Each cell is a possible
          offspring genotype, and each cell is equally likely. With multiple
          loci, multiply each locus probability together (loci segregate
          independently).
        </p>
        <p>
          The breeding calculator on this site does this automatically across
          all 13 modelled loci, including the ZW sex split.
        </p>
      </>
    ),
  },
];

export default function Genetics101() {
  return (
    <div data-testid="genetics101-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <header className="mb-12">
        <div className="overline">Primer 04 · Genetics 101</div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">
          A short primer for budgie breeders.
        </h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          The minimum you need to read genotypes and predict chicks — written
          in plain language, but accurate enough for the show bench.
        </p>
      </header>

      <div className="space-y-10">
        {SECTIONS.map((s) => (
          <section
            key={s.n}
            data-testid={`primer-section-${s.n}`}
            className="grid grid-cols-12 gap-6 border-t border-border pt-8"
          >
            <div className="col-span-12 md:col-span-3">
              <div className="font-mono text-xs text-muted-foreground">
                § {s.n}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <s.icon size={16} className="text-primary" />
                <h2 className="text-lg font-bold tracking-tight">{s.title}</h2>
              </div>
            </div>
            <div className="col-span-12 md:col-span-9 space-y-3 leading-relaxed">
              {s.body}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-14 border border-border bg-secondary p-6 lg:p-8 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div>
          <div className="overline">Ready to try it?</div>
          <p className="text-lg font-bold tracking-tight mt-1">
            Run a virtual pair and watch the inheritance math unfold.
          </p>
        </div>
        <Link
          to="/calculator"
          data-testid="primer-cta-calculator"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium"
        >
          Open the calculator <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
